import React from "react";
import {
  IX10ConfigChange,
  X10ConfigItemStatus,
  X10ConfigKey
} from "../../../../../store/x10config/types";
import { Chip, Dot } from "./configuration.styles";
import { statusText } from "./helpers";
import useConfigurationText from "./useConfigurationText";

export const formatDateTime = (iso: string | null | undefined) =>
  iso
    ? new Date(iso).toLocaleString(undefined, {
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit"
      })
    : "--";

export const formatAgo = (seconds: number | null | undefined) => {
  if (seconds === null || seconds === undefined) {
    return "--";
  }
  if (seconds < 90) {
    return `${seconds} s`;
  }
  if (seconds < 90 * 60) {
    return `${Math.round(seconds / 60)} min`;
  }
  if (seconds < 36 * 3600) {
    return `${Math.round(seconds / 3600)} h`;
  }
  return `${Math.round(seconds / 86400)} d`;
};

export const StatusChip: React.FC<{ configKey: X10ConfigKey; status: X10ConfigItemStatus }> = ({
  configKey,
  status
}) => {
  const t = useConfigurationText();
  const text = statusText(configKey, status);

  return (
    <Chip tone={text.tone} data-testid={`x10-config-status-${configKey}`}>
      <Dot tone={text.tone} />
      {t(text.key, text.fallback)}
    </Chip>
  );
};

/** Status of the last change of one key, and who made it when. */
const SyncStatus: React.FC<{ configKey: X10ConfigKey; change: IX10ConfigChange | null }> = ({
  configKey,
  change
}) => {
  const t = useConfigurationText();

  if (!change) {
    return <Chip tone="neutral">{t("never_changed", "Never changed from Control Tower")}</Chip>;
  }
  const source =
    change.source === "X10_APP"
      ? t("source_x10_app", "the owner app")
      : change.source === "BOOTUP_RESYNC" || change.source === "HEARTBEAT_RESYNC"
      ? t("source_resync", "jcore (re-sent)")
      : change.requestedBy || t("source_control_tower", "Control Tower");

  return (
    <span className="inline-flex flex-wrap items-center gap-xs">
      <StatusChip configKey={configKey} status={change.status} />
      <span className="text-grey-grey100 text-sm">
        {`${formatDateTime(change.sentAt || change.requestedAt)} · ${source}`}
      </span>
    </span>
  );
};

export default SyncStatus;
